import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { updateAttractionImage } from '~/services/AttractionsService';

import type { Attraction } from '~/types/attractionTypes';

import { setCacheBuster } from './attractionCacheBuster';
import { attractionQueryKeys } from './attractionQueryKeys';

/**
 * Sets the hero image for an attraction.
 *
 * Flow:
 *   1. onMutate: optimistic update so the new image renders immediately.
 *   2. onSuccess: store the backend `updatedAt` in sessionStorage so the
 *      next GET appends ?v={updatedAt} and bypasses CloudFront. Merge the
 *      mutation response into the detail cache, then invalidate detail
 *      and lists with the default refetch behaviour. The cache-buster
 *      makes the detail refetch a CDN miss, so it returns the fresh body.
 */
export function useUpdateAttractionImage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      attractionId,
      imageId,
      imageUrl,
    }: {
      attractionId: string;
      imageId: string;
      imageUrl: string;
    }) => {
      return await updateAttractionImage(attractionId, imageId, imageUrl);
    },
    onMutate: async ({ attractionId, imageUrl }) => {
      await queryClient.cancelQueries({ queryKey: attractionQueryKeys.detail(attractionId) });

      const previousAttraction = queryClient.getQueryData<Attraction>(attractionQueryKeys.detail(attractionId));

      if (previousAttraction) {
        queryClient.setQueryData<Attraction>(attractionQueryKeys.detail(attractionId), {
          ...previousAttraction,
          imageUrl,
        });
      }

      return { previousAttraction };
    },
    onSuccess: (data, { attractionId, imageUrl }) => {
      if (data.updatedAt) {
        setCacheBuster(attractionId, data.updatedAt);
      }

      queryClient.setQueryData<Attraction>(attractionQueryKeys.detail(attractionId), (prev) =>
        prev ? { ...prev, imageUrl } : prev,
      );

      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.detail(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });

      enqueueSnackbar('Hero image updated successfully', { variant: 'success' });
    },
    onError: (error: Error, { attractionId }, context) => {
      if (context?.previousAttraction) {
        queryClient.setQueryData(attractionQueryKeys.detail(attractionId), context.previousAttraction);
      }
      enqueueSnackbar(`Error updating image: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
